import differenceInMinutes from "~/kernel/calendar/differenceInMinutes";
import getCurrentDate from "./getCurrentDate";
import differenceInHours from "./differenceInHours";
import differenceInDays from "./differenceInDays";
import differenceInMonths from "./differenceInMonths";
import differenceInYears from "./differenceInYears";
import { HOURS_PER_DAY } from "./constants";
import type TranslateFunction from "~/kernel/internationalization/TranslateFunction";

type Options = {
  t: TranslateFunction;
  today?: Date;
};

export default function describeTimeSince(
  date: Date,
  { t, today = getCurrentDate() }: Options,
) {
  const minuteDiff = differenceInMinutes(today, date);
  const hourDiff = differenceInHours(today, date);
  const dayDiff = differenceInDays(today, date);
  const monthDiff = differenceInMonths(today, date);
  const yearDiff = differenceInYears(today, date);

  const describe = (key: string, count: number) =>
    t(`describeTimeSince.${key}`, { count: Math.abs(count) });

  if (yearDiff < 0) {
    return describe("inYears", yearDiff);
  }

  if (monthDiff < 0) {
    return describe("inMonths", monthDiff);
  }

  if (hourDiff <= -HOURS_PER_DAY) {
    return describe("inDays", dayDiff);
  }

  if (hourDiff < 0) {
    return describe("inHours", hourDiff);
  }

  if (minuteDiff < 0) {
    return describe("inMinutes", minuteDiff);
  }

  if (minuteDiff < 1) {
    return t("describeTimeSince.justNow");
  }

  if (hourDiff < 1) {
    return describe("minutesAgo", minuteDiff);
  }

  if (hourDiff < 24) {
    return describe("hoursAgo", hourDiff);
  }

  if (monthDiff < 1) {
    return describe("daysAgo", dayDiff);
  }

  if (yearDiff < 1) {
    return describe("monthsAgo", monthDiff);
  }

  return describe("yearsAgo", yearDiff);
}
